import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FontIcon } from '@fluentui/react/lib/Icon';

const getStyles = (_, { palette = {} }) => {
  const selectedIconStyle = {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: palette.accent,
    width: 18,
    height: 18,
    color: 'white',
    lineHeight: '18px',
    textAlign: 'center',
    borderRadius: '50%',
  };

  return {
    item: {
      position: 'relative',
      backgroundColor: '#F3F2F1',
      borderRadius: '4px',
      boxShadow: '1px 1px 1px 1px rgb(0, 0, 0, 0.12)',
    },
    selectedItem: {
      outline: `1.9px solid ${palette.accent}`,
    },
    selectedIcon: {
      ...selectedIconStyle,
    },
    selectedIconRadio: {
      ...selectedIconStyle,
      backgroundColor: 'white',
      color: palette.accent,
      outline: '0.5px solid',
    },
    displayNone: {
      display: 'none',
    },
    icon: {
      fontSize: 12,
      fontWeight: 'bold',
    },
    radioIcon: {
      fontSize: 30,
      fontWeight: 'bold',
      position: 'absolute',
      left: '-6px',
    },
    selectableItem: {
      cursor: 'pointer',
    },
    disabledItem: {
      filter: 'opacity(0.5)',
    },
  };
};

export const UnStyledSelectableItemView = ({
  item,
  classes,
  selectable,
  disabled,
  selected,
  onChange,
  className,
  unSelectedClassName,
  width,
  height,
  onRenderItem,
  ariaLabel,
  isMultiple,
}) => {
  const onClick = useCallback(() => {
    if (selectable) {
      if (isMultiple || !selected) {
        onChange(item);
      }
    }
  }, [selectable, isMultiple, selected, onChange, item]);

  const selectableProps = useMemo(() => (
    (selectable && !disabled) ? {
      onClick,
      onKeyDown: onChange,
      role: 'checkbox',
      'aria-checked': selected,
      'aria-label': ariaLabel,
      tabIndex: '0',
    } : {}
  ), [ariaLabel, onChange, onClick, selectable, selected, disabled]);

  return (
    <div
      className={classnames(
        className,
        classes.item,
        !selected && unSelectedClassName,
        selected && classnames(classes.selectedItem, 'selected-item'),
        selectable && !disabled && classnames(classes.selectableItem, 'selectable-item'),
        selectable && disabled && classnames(classes.disabledItem, 'disabled-item'),
        'selectable-item-base'
      )}
      style={{ width, height }}
      {...selectableProps}
    >
      {onRenderItem(item)}
      {
        selectable &&
        <div className={classnames(
          (isMultiple ? classes.selectedIcon : classes.selectedIconRadio),
          !selected && classes.displayNone
        )}
        >
          {
            isMultiple
              ? <FontIcon aria-label="Accept" iconName="Accept" className={classes.icon} />
              : <FontIcon aria-label="RadioBullet" iconName="RadioBullet" className={classes.radioIcon} />
          }
        </div>
      }
    </div>
  );
};

UnStyledSelectableItemView.propTypes = {
  classes: PropTypes.shape({
    item: PropTypes.string.isRequired,
  }).isRequired,
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string,
  unSelectedClassName: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  onRenderItem: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  selectable: PropTypes.bool,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  ariaLabel: PropTypes.string,
  isMultiple: PropTypes.bool,
};

UnStyledSelectableItemView.defaultProps = {
  selectable: false,
  selected: false,
  onChange: noop,
  width: undefined,
  height: undefined,
  className: undefined,
  unSelectedClassName: undefined,
  ariaLabel: null,
  isMultiple: true,
  disabled: false,
};

export const SelectableItemView = withDefaultStyles(
  UnStyledSelectableItemView,
  getStyles
);
